import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

export function ChangePasswordDialog({ trigger, open: openProp, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (o) => { setOpenState(o); onOpenChange?.(o); };
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (next !== confirm) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await api.put("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata");
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="w-5 h-5 text-cyan-400" /> Cambia password</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Inserisci la password attuale e la nuova password (min. 8 caratteri).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cp-current">Password attuale</Label>
            <PasswordInput id="cp-current" data-testid="cp-current-input" value={current}
              onChange={(e) => setCurrent(e.target.value)} required className="bg-slate-800 border-slate-700" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-new">Nuova password</Label>
            <PasswordInput id="cp-new" data-testid="cp-new-input" value={next}
              onChange={(e) => setNext(e.target.value)} required className="bg-slate-800 border-slate-700" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm">Conferma nuova password</Label>
            <PasswordInput id="cp-confirm" data-testid="cp-confirm-input" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required className="bg-slate-800 border-slate-700" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="cp-submit-btn"
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Aggiorna password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
